/**
 * Phase 17 — modal editor for RemodelIntent catalog rows.
 *
 * Create + edit modes. Tenant admin overrides the platform-default
 * set (Kitchen / Bathroom / Siding / Patio / Other) or adds new
 * intents. Mirrors `MaterialEditor` for visual parity.
 */

import { useState } from "react";

import {
  createRemodelIntent,
  patchRemodelIntent,
} from "@starz/shared/api/sdk.gen";
import type {
  RemodelIntent,
  RemodelIntentCreateBody,
  RemodelIntentPatchBody,
} from "@starz/shared/api/types.gen";

interface Props {
  intent: RemodelIntent | null;
  onCancel: () => void;
  onSaved: (intent: RemodelIntent) => void;
}

export function IntentEditor({ intent, onCancel, onSaved }: Props) {
  const isCreate = intent === null;
  const [intentId, setIntentId] = useState(intent?.intent_id ?? "");
  const [displayName, setDisplayName] = useState(intent?.display_name ?? "");
  const [photoUrl, setPhotoUrl] = useState(intent?.photo_url ?? "");
  const [defaultRoomType, setDefaultRoomType] = useState(intent?.default_room_type ?? "");
  const [sortOrder, setSortOrder] = useState(String(intent?.sort_order ?? 100));
  const [active, setActive] = useState(intent?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError("Display name is required.");
      return;
    }
    const order = Number.parseInt(sortOrder, 10);
    if (!Number.isFinite(order) || order < 0) {
      setError("Sort order must be a non-negative integer.");
      return;
    }
    setBusy(true);
    if (isCreate) {
      if (!/^[a-z0-9_]+$/.test(intentId)) {
        setBusy(false);
        setError("intent_id must be lowercase letters / digits / underscores.");
        return;
      }
      const body: RemodelIntentCreateBody = {
        intent_id: intentId,
        display_name: displayName.trim(),
        photo_url: photoUrl.trim() || null,
        default_room_type: defaultRoomType.trim() || null,
        sort_order: order,
        active,
      };
      const result = await createRemodelIntent({ body });
      setBusy(false);
      if ("error" in result) {
        setError(
          result.error.error.code === "CONFLICT"
            ? "An intent with this ID already exists."
            : result.error.error.message,
        );
        return;
      }
      onSaved(result.data);
    } else {
      const body: RemodelIntentPatchBody = {
        display_name: displayName.trim(),
        photo_url: photoUrl.trim() || null,
        default_room_type: defaultRoomType.trim() || null,
        sort_order: order,
        active,
      };
      const result = await patchRemodelIntent({
        intentId: intent!.intent_id,
        ifMatchVersion: intent!.version,
        body,
      });
      setBusy(false);
      if ("error" in result) {
        setError(
          result.error.error.code === "CONFLICT"
            ? "Intent changed elsewhere — reload and retry."
            : result.error.error.message,
        );
        return;
      }
      onSaved(result.data);
    }
  }

  const inputClass =
    "mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-starz-charcoal/60 p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-xl space-y-3 rounded-2xl border border-starz-gold/30 bg-white p-6 shadow-xl"
      >
        <h2 className="font-serif text-xl text-starz-navy">
          {isCreate ? "Add intent" : `Edit ${intent!.display_name}`}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
              ID {isCreate ? "" : "(read-only)"}
            </label>
            <input
              type="text"
              value={intentId}
              onChange={(e) => isCreate && setIntentId(e.target.value)}
              disabled={!isCreate}
              className={`${inputClass} ${isCreate ? "" : "bg-starz-cream text-warm-gray"}`}
              placeholder="kitchen"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
              Display name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
              Photo URL (optional)
            </label>
            <input
              type="text"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              className={inputClass}
              placeholder="https://…"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
              Default room type
            </label>
            <input
              type="text"
              value={defaultRoomType}
              onChange={(e) => setDefaultRoomType(e.target.value)}
              className={inputClass}
              placeholder="kitchen"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
              Sort order
            </label>
            <input
              type="number"
              min="0"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-starz-charcoal">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4"
          />
          Active
        </label>
        {error ? (
          <p className="border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-starz-gold/30 px-3 py-1.5 text-xs font-medium text-starz-charcoal hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-starz-navy px-4 py-1.5 text-xs font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
